import { create } from 'zustand';
import type { CurrentUser, Permission } from '../api/types';

const STORAGE_KEY = 'siraj.auth.v1';

interface Persisted {
  token: string | null;
  user: CurrentUser | null;
  orgId: string | null;
}

function read(): Persisted {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw) as Persisted;
  } catch {
    /* ignoré */
  }
  return { token: null, user: null, orgId: null };
}

function write(p: Persisted) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(p));
  } catch {
    /* ignoré */
  }
}

interface AuthState extends Persisted {
  setSession: (token: string, user: CurrentUser) => void;
  setUser: (user: CurrentUser) => void;
  setOrgId: (orgId: string) => void;
  clear: () => void;
}

/** État d'authentification persisté (jeton, utilisateur, organisation active). */
export const useAuthStore = create<AuthState>((set, get) => ({
  ...read(),
  setSession: (token, user) => {
    // Organisation active : on garde la précédente si l'utilisateur y appartient encore
    const prev = get().orgId;
    const orgId = user.memberships.some((m) => m.orgId === prev) ? prev : (user.memberships[0]?.orgId ?? null);
    const next = { token, user, orgId };
    write(next);
    set(next);
  },
  setUser: (user) => {
    const { token, orgId } = get();
    const valid = user.memberships.some((m) => m.orgId === orgId) ? orgId : (user.memberships[0]?.orgId ?? null);
    write({ token, user, orgId: valid });
    set({ user, orgId: valid });
  },
  setOrgId: (orgId) => {
    write({ token: get().token, user: get().user, orgId });
    set({ orgId });
  },
  clear: () => {
    const next = { token: null, user: null, orgId: get().orgId };
    write(next);
    set({ token: null, user: null });
  },
}));

export function useIsAuthenticated() {
  return useAuthStore((s) => Boolean(s.token && s.user));
}

/** Membership de l'organisation active. */
export function useCurrentMembership() {
  return useAuthStore((s) => s.user?.memberships.find((m) => m.orgId === s.orgId) ?? null);
}

/** Identifiant de l'organisation active (jette si aucune : à utiliser dans les écrans protégés). */
export function useOrgId(): string {
  const id = useAuthStore((s) => s.orgId);
  if (!id) throw new Error('Aucune organisation active');
  return id;
}

export function hasPermission(permissions: readonly Permission[] | undefined, action: Permission): boolean {
  return Boolean(permissions?.includes(action));
}
